import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { VideoCamera } from '@phosphor-icons/react';

export const MeetingPlugins = ({ tenantId }) => {
  const qc = useQueryClient();
  const { data: plugins } = useQuery({
    queryKey: ['meeting-plugins', tenantId],
    queryFn: () => api.get('/meetings/plugins', { params: { tenant_id: tenantId } }).then((r) => r.data),
    enabled: !!tenantId,
  });
  const { data: gstatus } = useQuery({
    queryKey: ['gcal-status', tenantId],
    queryFn: () => api.get('/meetings/google-status', { params: { tenant_id: tenantId } }).then((r) => r.data),
    enabled: !!tenantId,
  });
  const [creds, setCreds] = useState({});
  const saveMut = useMutation({
    mutationFn: (body) => api.post('/meetings/plugins', { tenant_id: tenantId, ...body }),
    onSuccess: () => { toast.success('Plugin saved'); setCreds({}); qc.invalidateQueries({ queryKey: ['meeting-plugins', tenantId] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const connectMut = useMutation({
    mutationFn: () => api.get('/oauth/google-calendar/start', { params: { tenant_id: tenantId } }),
    onSuccess: (r) => { window.open(r.data.authorization_url, 'gcal', 'width=520,height=640'); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const disconnectMut = useMutation({
    mutationFn: () => api.delete('/meetings/google-disconnect', { params: { tenant_id: tenantId } }),
    onSuccess: () => { toast.success('Google Calendar disconnected'); qc.invalidateQueries({ queryKey: ['gcal-status', tenantId] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const [gcfg, setGcfg] = useState({ client_id: '', client_secret: '' });
  const gcfgMut = useMutation({
    mutationFn: () => api.post('/meetings/google-config', gcfg),
    onSuccess: () => { toast.success('Google OAuth keys saved - now hit Connect'); setGcfg({ client_id: '', client_secret: '' }); qc.invalidateQueries({ queryKey: ['gcal-status', tenantId] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div className="max-w-2xl bg-white border border-slate-200 rounded-lg p-6" data-testid="meeting-plugins-panel">
      <div className="rounded-lg border border-orange-200 bg-orange-50/50 p-4 mb-5" data-testid="gcal-connect-card">
        <div className="flex items-center gap-2 mb-1">
          <VideoCamera size={18} className="text-orange-600" weight="fill" />
          <h3 className="font-display font-semibold">Real Google Meet rooms</h3>
          <span className={`ms-auto font-mono-label text-[9px] uppercase px-2 py-0.5 rounded ${gstatus?.connected ? 'bg-green-100 text-green-800' : gstatus?.configured ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-500'}`} data-testid="gcal-status-badge">
            {gstatus?.connected ? `connected: ${gstatus.connected_email || 'ok'}` : gstatus?.configured ? 'not connected' : 'not configured'}
          </span>
        </div>
        <p className="text-sm text-slate-600 mb-3">Connect a Google account once so confirming a meeting creates a genuine Meet link and emails calendar invites. Paste your Google OAuth keys below (Google Cloud Console - Calendar API - OAuth Client, redirect URI <code className="text-[10px] bg-white px-1 rounded border border-orange-200">{'{BASE}'}/api/oauth/google-calendar/callback</code>), then Connect.</p>
        <div className="flex gap-2 flex-wrap mb-3">
          <Input data-testid="gcal-client-id-input" className="w-64 h-8 text-xs bg-white" placeholder={gstatus?.client_id_masked ? `Client ID (${gstatus.client_id_masked})` : 'Google OAuth Client ID'}
            value={gcfg.client_id} onChange={(e) => setGcfg({ ...gcfg, client_id: e.target.value })} />
          <Input data-testid="gcal-client-secret-input" type="password" className="w-52 h-8 text-xs bg-white" placeholder="Client secret"
            value={gcfg.client_secret} onChange={(e) => setGcfg({ ...gcfg, client_secret: e.target.value })} />
          <Button data-testid="gcal-save-config" size="sm" className="h-8 bg-slate-900 hover:bg-slate-800" disabled={!gcfg.client_id || !gcfg.client_secret || gcfgMut.isPending}
            onClick={() => gcfgMut.mutate()}>Save keys</Button>
        </div>
        <div className="flex gap-2">
          <Button data-testid="gcal-connect-button" size="sm" disabled={!gstatus?.configured || connectMut.isPending} onClick={() => connectMut.mutate()} className="bg-orange-600 hover:bg-orange-500">
            {gstatus?.connected ? 'Reconnect Google' : 'Connect Google Calendar'}
          </Button>
          {gstatus?.connected && (
            <Button data-testid="gcal-disconnect-button" size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => disconnectMut.mutate()}>Disconnect</Button>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 mb-1">
        <VideoCamera size={18} className="text-orange-600" weight="duotone" />
        <h2 className="font-display text-lg font-semibold">Meeting plugins</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4">Enable video-call providers for parent-teacher meetings requested from chat. Without API credentials, confirmed meetings get valid demo join links; paste real credentials to go live.</p>
      <div className="divide-y divide-slate-100">
        {(plugins || []).map((p) => {
          const c = creds[p.provider] || {};
          return (
            <div key={p.provider} className="py-3" data-testid={`plugin-row-${p.provider}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-medium text-sm w-36">{p.label}</span>
                <span className={`font-mono-label text-[9px] uppercase px-2 py-0.5 rounded ${p.enabled ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'}`} data-testid={`plugin-status-${p.provider}`}>
                  {p.enabled ? (p.live ? 'live' : 'enabled · demo links') : 'disabled'}
                </span>
                <Button data-testid={`plugin-toggle-${p.provider}`} size="sm" variant="outline" className="ml-auto"
                  onClick={() => saveMut.mutate({ provider: p.provider, enabled: !p.enabled })}>
                  {p.enabled ? 'Disable' : 'Enable'}
                </Button>
              </div>
              <div className="flex gap-2 mt-2 flex-wrap">
                <Input data-testid={`plugin-client-id-${p.provider}`} className="w-52 h-8 text-xs" placeholder={p.client_id_masked ? `Client ID (${p.client_id_masked})` : 'Client ID (optional)'}
                  value={c.client_id || ''} onChange={(e) => setCreds({ ...creds, [p.provider]: { ...c, client_id: e.target.value } })} />
                <Input data-testid={`plugin-client-secret-${p.provider}`} type="password" className="w-52 h-8 text-xs" placeholder="Client secret (optional)"
                  value={c.client_secret || ''} onChange={(e) => setCreds({ ...creds, [p.provider]: { ...c, client_secret: e.target.value } })} />
                <Button data-testid={`plugin-save-creds-${p.provider}`} size="sm" disabled={!c.client_id && !c.client_secret}
                  onClick={() => saveMut.mutate({ provider: p.provider, enabled: true, ...c })}
                  className="bg-slate-900 hover:bg-slate-800 h-8">Save creds</Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
